import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDateTime, formatNumber } from "@/lib/format";

type Conversation = components["schemas"]["AdminConversationResponse"];
type AgentType = components["schemas"]["AgentType"];

interface AgentConversationsTableProps {
  organisationId: string;
}

const agentTypeFilterOptions: { value: AgentType | "any"; label: string }[] = [
  { value: "any", label: "All agents" },
  { value: "Ar", label: "AR" },
  { value: "Ap", label: "AP" },
  { value: "Books", label: "Books" },
];

const agentTypeBadgeVariant: Record<AgentType, "default" | "secondary" | "outline"> = {
  Ar: "default",
  Ap: "secondary",
  All: "outline",
  Books: "outline",
};

export function AgentConversationsTable({ organisationId }: AgentConversationsTableProps) {
  const [agentType, setAgentType] = useState<AgentType | "any">("any");
  const [userIdInput, setUserIdInput] = useState("");
  const debouncedUserId = useDebouncedValue(userIdInput);

  const pagination = useCursorPagination({ initialLimit: 20 });

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const prevFiltersRef = useRef({ agentType, debouncedUserId });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (prev.agentType !== agentType || prev.debouncedUserId !== debouncedUserId) {
      prevFiltersRef.current = { agentType, debouncedUserId };
      pagination.reset();
    }
  }, [agentType, debouncedUserId, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchConversations() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET(
        "/admin/organisations/{organisationId}/agent/conversations",
        {
          params: {
            path: { organisationId },
            query: {
              AgentType: agentType === "any" ? undefined : agentType,
              UserId: debouncedUserId || undefined,
              Cursor: pagination.cursor,
              Limit: pagination.limit,
            },
          },
        },
      );
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load conversations.");
      } else {
        setConversations(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchConversations();
    return () => {
      cancelled = true;
    };
  }, [organisationId, agentType, debouncedUserId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Select value={agentType} onValueChange={(v) => setAgentType(v as AgentType | "any")}>
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {agentTypeFilterOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Filter by user ID..."
          value={userIdInput}
          onChange={(e) => setUserIdInput(e.target.value)}
          className="max-w-sm font-mono text-xs"
        />
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>User</TableHead>
              <TableHead className="text-right">Messages</TableHead>
              <TableHead className="text-right">Tokens (in / out)</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {conversations.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground h-24 text-center">
                  No conversations found.
                </TableCell>
              </TableRow>
            ) : (
              conversations.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">
                    {c.id ? (
                      <Link
                        to={`/agent/conversations/${c.id}`}
                        className="hover:underline"
                      >
                        {c.title || <span className="text-muted-foreground italic">Untitled</span>}
                      </Link>
                    ) : (
                      c.title || <span className="text-muted-foreground italic">Untitled</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.agentType && (
                      <Badge variant={agentTypeBadgeVariant[c.agentType] ?? "outline"}>
                        {c.agentType}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.userId ? (
                      <Link to={`/users/${c.userId}`}>
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {c.userId.slice(0, 8)}…
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(c.messageCount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
                    {formatNumber(c.totalInputTokens)} / {formatNumber(c.totalOutputTokens)}
                  </TableCell>
                  <TableCell>{formatDateTime(c.updatedAt)}</TableCell>
                  <TableCell>{formatDateTime(c.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
